import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  Article,
  Event,
  HealthAndSafety,
  RateReview,
  Refresh,
  VideoLibrary,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { formatTranslatedLabel } from '../i18n';
import { useLanguage } from '../LanguageContext';

interface ServiceStatus {
  labelKey: string;
  url: string;
  status: 'healthy' | 'unhealthy';
  icon: React.ReactNode;
}

const serviceDefinitions = [
  { labelKey: 'platformService', url: '/api/platform/health', icon: <AccountTree /> },
  { labelKey: 'videoService', url: '/api/video/health', icon: <VideoLibrary /> },
  { labelKey: 'interviewService', url: '/api/interview/health', icon: <Event /> },
  { labelKey: 'storyService', url: '/api/story/health', icon: <Article /> },
  { labelKey: 'reviewService', url: '/api/review/health', icon: <RateReview /> },
];

const OperationsStatus: React.FC = () => {
  const { t } = useLanguage();
  const [statuses, setStatuses] = useState<ServiceStatus[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadStatuses = useCallback(async () => {
    try {
      setError(null);
      const results = await Promise.all(
        serviceDefinitions.map(async (service) => {
          try {
            const response = await apiClient.get(service.url);
            return {
              ...service,
              status: response.data.status === 'healthy' ? 'healthy' : 'unhealthy',
            } as ServiceStatus;
          } catch (err) {
            return { ...service, status: 'unhealthy' } as ServiceStatus;
          }
        })
      );
      setStatuses(results);
    } catch (err) {
      setError(t('couldNotCheckServiceStatus'));
    }
  }, [t]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('operations')}
          </Typography>
          <Typography color="text.secondary">
            {t('operationsDescription')}
          </Typography>
        </Box>
        <IconButton aria-label={t('refreshServiceHealth')} onClick={loadStatuses}>
          <Refresh />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <List>
          {statuses.map((service) => (
            <ListItem
              key={service.labelKey}
              secondaryAction={
                <Chip
                  size="small"
                  label={formatTranslatedLabel(t, service.status)}
                  color={service.status === 'healthy' ? 'success' : 'error'}
                />
              }
            >
              <ListItemIcon>{service.icon || <HealthAndSafety />}</ListItemIcon>
              <ListItemText primary={t(service.labelKey)} secondary={service.url} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default OperationsStatus;
